import React from 'react';
import { motion } from 'framer-motion';
import { Download, Clock, Star, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { WRITING_SAMPLES } from '../mock/data';

const WritingSamples = () => {
  const { t } = useLanguage();

  const handleDownload = (sample) => {
    if (!sample.available || !sample.url) return;
    const a = document.createElement('a');
    a.href = sample.url;
    a.download = sample.filename || 'document.pdf';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <section id="writing" className="section relative paper-texture">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="label-small mb-4">{t.writing.label}</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
            {t.writing.heading}
          </h2>
          <div className="gold-rule mt-6 mb-6" />
          <p className="text-[#DCCFAD] text-lg italic font-heading">
            {t.writing.subheading}
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {WRITING_SAMPLES.map((sample, i) => (
            <motion.article
              key={sample.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
              className={`relative group flex flex-col border bg-[rgba(196,163,90,0.025)] p-7 transition-all duration-500 ${
                sample.available
                  ? 'border-[rgba(196,163,90,0.25)] hover:border-[#C4A35A] hover:bg-[rgba(196,163,90,0.05)] hover:-translate-y-1'
                  : 'border-[rgba(196,163,90,0.1)] opacity-70'
              }`}
              style={{ minHeight: '340px' }}
            >
              {/* Corner ornaments */}
              <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-[rgba(196,163,90,0.5)]" />
              <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-[rgba(196,163,90,0.5)]" />
              <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-[rgba(196,163,90,0.5)]" />
              <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-[rgba(196,163,90,0.5)]" />

              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2 text-[#C4A35A]">
                  <FileText size={14} />
                  <span className="text-[10px] tracking-[0.4em] uppercase">{sample.year}</span>
                </div>
                {sample.featured && (
                  <div className="flex items-center gap-1.5 text-[#C4A35A]">
                    <Star size={12} fill="#C4A35A" />
                    <span className="text-[9px] tracking-[0.35em] uppercase">{t.writing.featured}</span>
                  </div>
                )}
              </div>

              <div className="text-[10px] tracking-[0.3em] uppercase text-[#8a7a54] mb-3">
                {sample.category}
              </div>
              <h3 className="font-heading text-2xl text-[#F0EDE8] leading-tight mb-4 group-hover:text-[#C4A35A] transition-colors">
                {sample.title}
              </h3>
              <p className="text-[#DCCFAD] text-sm leading-relaxed flex-1">
                {sample.snippet}
              </p>

              <div className="mt-6 pt-4 border-t border-[rgba(196,163,90,0.15)]">
                {sample.available ? (
                  <button onClick={() => handleDownload(sample)} className="btn-outline-gold w-full">
                    <Download size={13} />
                    {t.writing.download}
                  </button>
                ) : (
                  <button disabled className="btn-outline-gold w-full">
                    <Clock size={13} />
                    {t.writing.comingSoon}
                  </button>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WritingSamples;
